// Pure SVG renderers used for BOTH browser PDF (svg2pdf) and server PDF (cairosvg),
// and embedded into draw.io exports for the auto pages. A3 landscape sheet.
import { iconInner, deviceLabel } from './deviceIcons';
import { subnetInfo } from './subnet';
import { connType as getConnType, connLabel, connSvgAttrs, computeSideBundles, computeParallelLanes } from './connections';
import { smoothStepPoints, pointsToRoundedPath, laneCenter } from './orthoRoute';
import { pageDisplayName } from './pageNames';
import { computeDetailModel, DLAYOUT, detailPortCenterY, detailBoxHeight, detailRowHeight, detailRowFont, deviceDisplayName, portNameGlobal, pickEdgeSides, findDeviceGlobal, getAllEdges, buildNodeResolver, displayEdgesForPage } from './detail';
import { computePanelLayout, computePanelSideHints, pickBothSidesSide, P, wrapText } from './panel';
import { resolvePortVlans, resolvedPortTitle } from './vlanPorts';
import { safeImageHref, sanitizeDocImages } from './safeUri';

export const SHEET = { w: 1587, h: 1123 };
const OUTER = 26;
export const INNER = 40;
const GRID = 120;
const INK = '#111418';
const MUTED = '#5b6672';
const FRAME = '#1b232c';
const WARN = '#B45309'; // amber-700: Design-Check Warnung / Konflikt

export const NODE_W = 64;
export const ICON = 56;

function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function trunc(s, n) {
  s = String(s == null ? '' : s);
  return s.length > n ? s.slice(0, n - 1) + '\u2026' : s;
}

// Small amber warning triangle with an exclamation mark. Drawn as vector paths
// (font-independent) so it renders reliably in both svg2pdf and cairosvg.
function warnTri(x, y, s = 12) {
  return (
    `<path d="M${x + s / 2},${y} L${x + s},${y + s} L${x},${y + s} Z" fill="${WARN}"/>` +
    `<text x="${x + s / 2}" y="${y + s - 1.5}" text-anchor="middle" font-family="IBM Plex Sans, sans-serif" font-size="${(s * 0.72).toFixed(1)}" font-weight="700" fill="#ffffff">!</text>`
  );
}

// Word-wrap a label into up to `maxLines` lines of at most `maxChars` chars.
// Long single words are hard-split; overflow beyond maxLines is ellipsised.
function wrapLabelLines(text, maxChars, maxLines = 2) {
  const t = String(text == null ? '' : text).trim();
  if (!t) return [];
  const words = t.split(/\s+/);
  const all = [];
  let cur = '';
  for (let w of words) {
    while (w.length > maxChars) {
      if (cur) {
        all.push(cur);
        cur = '';
      }
      all.push(w.slice(0, maxChars));
      w = w.slice(maxChars);
    }
    const tryLine = cur ? cur + ' ' + w : w;
    if (tryLine.length <= maxChars) cur = tryLine;
    else {
      if (cur) all.push(cur);
      cur = w;
    }
  }
  if (cur) all.push(cur);
  if (all.length <= maxLines) return all;
  const kept = all.slice(0, maxLines);
  kept[maxLines - 1] = trunc(kept[maxLines - 1] + ' ', maxChars);
  return kept;
}

function iconAt(deviceType, x, y, size) {
  const sc = size / 64;
  return `<g transform="translate(${x},${y}) scale(${sc})">${iconInner(deviceType)}</g>`;
}

function usedConnTypes(doc) {
  const set = new Set();
  (doc.pages || []).forEach((p) => (p.edges || []).forEach((e) => set.add(e.connType || 'ethernet')));
  const arr = Array.from(set);
  return arr.length ? arr : ['ethernet', 'lwl'];
}

function tbLabels(lang) {
  if (lang === 'en') {
    return { projekt: 'PROJECT', titel: 'TITLE', znr: 'DRAWING NO.', datum: 'DATE', blatt: 'SHEET', ersteller: 'DRAWN', geprueft: 'CHECKED', rev: 'REV.' };
  }
  return { projekt: 'PROJEKT', titel: 'TITEL', znr: 'ZEICHNUNGSNR.', datum: 'DATUM', blatt: 'BLATT', ersteller: 'ERSTELLER', geprueft: 'GEPR\u00dcFT', rev: 'REV.' };
}

function cell(x, y, w, h, label, value, mono = true) {
  const valFont = mono ? "font-family='IBM Plex Mono, monospace'" : "font-family='IBM Plex Sans, sans-serif'";
  return (
    `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="none" stroke="${FRAME}" stroke-width="1"/>` +
    `<text x="${x + 9}" y="${y + 15}" font-family="IBM Plex Sans, sans-serif" font-size="9.5" letter-spacing="0.6" fill="${MUTED}">${esc(label)}</text>` +
    `<text x="${x + 9}" y="${y + h - 11}" ${valFont} font-size="13" fill="${INK}">${esc(trunc(value, Math.floor(w / 8)))}</text>`
  );
}

function titleBlock(doc, lang, pageInfo) {
  const tb = doc.titleBlock || {};
  const L = tbLabels(lang);
  const tbW = 560;
  const tbH = 168;
  const tbX = SHEET.w - INNER - tbW;
  const tbY = SHEET.h - INNER - tbH;
  const logoW = 170;
  const infoX = tbX + logoW;
  const infoW = tbW - logoW; // 390
  const rowH = tbH / 4; // 42

  let s = `<rect x="${tbX}" y="${tbY}" width="${tbW}" height="${tbH}" fill="#ffffff" stroke="${FRAME}" stroke-width="1.6"/>`;
  s += `<line x1="${infoX}" y1="${tbY}" x2="${infoX}" y2="${tbY + tbH}" stroke="${FRAME}" stroke-width="1"/>`;
  const logoHref = safeImageHref(tb.logo);
  if (logoHref) {
    s += `<image x="${tbX + 14}" y="${tbY + 14}" width="${logoW - 28}" height="${tbH - 54}" href="${esc(logoHref)}" preserveAspectRatio="xMidYMid meet"/>`;
  }
  s += `<line x1="${tbX}" y1="${tbY + tbH - 30}" x2="${infoX}" y2="${tbY + tbH - 30}" stroke="${FRAME}" stroke-width="0.8"/>`;
  s += `<text x="${tbX + logoW / 2}" y="${tbY + tbH - 11}" text-anchor="middle" font-family="IBM Plex Mono, monospace" font-size="12" fill="${INK}">${esc(trunc(tb.firma || '', 22))}</text>`;

  s += cell(infoX, tbY, infoW, rowH, L.projekt, tb.projekt, false);
  s += cell(infoX, tbY + rowH, infoW, rowH, L.titel, tb.titel, false);
  s += cell(infoX, tbY + 2 * rowH, 160, rowH, L.znr, tb.zeichnungsnummer);
  s += cell(infoX + 160, tbY + 2 * rowH, 110, rowH, L.datum, tb.datum);
  s += cell(infoX + 270, tbY + 2 * rowH, 120, rowH, L.blatt, `${pageInfo.index} / ${pageInfo.total}`);
  s += cell(infoX, tbY + 3 * rowH, 130, rowH, L.ersteller, tb.ersteller, false);
  s += cell(infoX + 130, tbY + 3 * rowH, 130, rowH, L.geprueft, tb.geprueft, false);
  s += cell(infoX + 260, tbY + 3 * rowH, 130, rowH, L.rev, tb.revision);
  return s;
}

function legendBox(types, lang) {
  if (!types || !types.length) return '';
  const rowH = 20;
  const w = 236;
  const h = 30 + types.length * rowH;
  const x = INNER + 12;
  const y = SHEET.h - INNER - h - 8;
  let s = `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="6" fill="#ffffff" stroke="${FRAME}" stroke-width="1"/>`;
  s += `<text x="${x + 12}" y="${y + 19}" font-family="IBM Plex Sans, sans-serif" font-size="11" font-weight="600" letter-spacing="0.7" fill="${INK}">${esc(lang === 'en' ? 'LEGEND' : 'LEGENDE')}</text>`;
  types.forEach((key, i) => {
    const ry = y + 30 + i * rowH + 8;
    s += `<line x1="${x + 12}" y1="${ry}" x2="${x + 54}" y2="${ry}" fill="none" ${connSvgAttrs(key)} stroke-linecap="round"/>`;
    s += `<text x="${x + 64}" y="${ry + 4}" font-family="IBM Plex Sans, sans-serif" font-size="11" fill="${INK}">${esc(connLabel(key, lang))}</text>`;
  });
  return s;
}

function cadFrame(doc, lang, pageInfo, heading, legendTypes, opts = {}) {
  const innerW = SHEET.w - 2 * INNER;
  const innerH = SHEET.h - 2 * INNER;
  let s = '';
  if (!opts.transparent) {
    s += `<rect x="0" y="0" width="${SHEET.w}" height="${SHEET.h}" fill="#ffffff"/>`;
    let grid = '';
    for (let gx = INNER + GRID; gx < INNER + innerW; gx += GRID) {
      grid += `<line x1="${gx}" y1="${INNER}" x2="${gx}" y2="${INNER + innerH}"/>`;
    }
    for (let gy = INNER + GRID; gy < INNER + innerH; gy += GRID) {
      grid += `<line x1="${INNER}" y1="${gy}" x2="${INNER + innerW}" y2="${gy}"/>`;
    }
    s += `<g stroke="${INK}" stroke-opacity="0.05" stroke-width="1">${grid}</g>`;
  }
  s += `<rect x="${OUTER}" y="${OUTER}" width="${SHEET.w - 2 * OUTER}" height="${SHEET.h - 2 * OUTER}" fill="none" stroke="${FRAME}" stroke-width="2"/>`;
  s += `<rect x="${INNER}" y="${INNER}" width="${innerW}" height="${innerH}" fill="none" stroke="${FRAME}" stroke-width="1"/>`;
  if (heading) {
    s += `<text x="${INNER + 16}" y="${INNER + 30}" font-family="IBM Plex Sans, sans-serif" font-size="20" font-weight="600" fill="${INK}">${esc(heading)}</text>`;
    s += `<line x1="${INNER + 16}" y1="${INNER + 42}" x2="${INNER + 360}" y2="${INNER + 42}" stroke="${FRAME}" stroke-width="1"/>`;
  }
  if (legendTypes && legendTypes.length) s += legendBox(legendTypes, lang);
  s += titleBlock(doc, lang, pageInfo);
  return s;
}

// Ordered label lines of a device: TYPE (small, only if a custom name is set)
// -> NAME (wraps up to 2 lines) -> sub lines (VLAN, IP).
function nodeLabelLines(node, doc, lang, labelPos) {
  const vlan = doc.vlans.find((v) => v.id === node.data?.vlanRef);
  const name = node.data?.label || deviceLabel(node.data?.deviceType, lang);
  const typeText = deviceLabel(node.data?.deviceType, lang);
  const showType = name !== typeText;
  const ip = node.data?.ip || '';
  const vlanText = vlan ? 'VLAN' + vlan.vlanId : '';
  const subLines = [vlanText, ip].filter(Boolean);
  const nameMax = labelPos === 'left' ? 24 : 22;
  const nameLines = wrapLabelLines(name, nameMax, 2);
  const lines = [];
  if (showType) lines.push({ t: typeText, kind: 'type' });
  nameLines.forEach((ln) => lines.push({ t: ln, kind: 'name' }));
  subLines.forEach((ln) => lines.push({ t: trunc(ln, labelPos === 'left' ? 26 : 30), kind: 'sub' }));
  return lines;
}

// Extent of the label block in the direction a cable would cross it: the
// label HEIGHT below the card (labelPos 'below') or its WIDTH left of the card
// (labelPos 'left'). Used to push port chips past the label on that side.
function labelBlockExtent(node, doc, lang, labelPos) {
  const lines = nodeLabelLines(node, doc, lang, labelPos);
  if (!lines.length) return 0;
  if (labelPos === 'left') {
    const charW = { type: 5.6, name: 7.2, sub: 6.1 };
    return Math.max(...lines.map((L) => L.t.length * charW[L.kind])) + 8;
  }
  // below: first baseline at y+70 (card bottom at y+62) + line advances
  return 8 + lines.reduce((h, L) => h + (L.kind === 'sub' ? 12.5 : 13.5), 0);
}

function nodeSvg(node, doc, lang, labelPos) {
  const x = Math.round(node.position?.x || 0);
  const y = Math.round(node.position?.y || 0);
  const cx = x + NODE_W / 2;
  const iconX = x + (NODE_W - ICON) / 2;
  const iconY = y + 2;
  const vlan = doc.vlans.find((v) => v.id === node.data?.vlanRef);
  const name = node.data?.label || deviceLabel(node.data?.deviceType, lang);
  const typeText = deviceLabel(node.data?.deviceType, lang);
  const showType = name !== typeText;
  const ip = node.data?.ip || '';
  const vlanText = vlan ? 'VLAN' + vlan.vlanId : '';
  let s = `<rect x="${x}" y="${iconY - 4}" width="${NODE_W}" height="${NODE_W}" rx="8" fill="#ffffff" stroke="#1b232c" stroke-opacity="0.5" stroke-width="1.5"/>`;
  s += iconAt(node.data?.deviceType, iconX, iconY, ICON);
  if (vlan) {
    s += `<circle cx="${x + NODE_W - 3}" cy="${iconY - 1}" r="5.5" fill="${vlan.color}" stroke="#ffffff" stroke-width="1.5"/>`;
  }
  const lines = nodeLabelLines(node, doc, lang, labelPos);
  const drawLine = (L, tx, ty, anchor) => {
    if (L.kind === 'type')
      return `<text x="${tx}" y="${ty}" text-anchor="${anchor}" font-family="IBM Plex Sans, sans-serif" font-size="9" font-weight="600" letter-spacing="0.5" fill="${MUTED}">${esc(L.t.toUpperCase())}</text>`;
    if (L.kind === 'name')
      return `<text x="${tx}" y="${ty}" text-anchor="${anchor}" font-family="IBM Plex Sans, sans-serif" font-size="12.5" font-weight="600" fill="${INK}">${esc(L.t)}</text>`;
    return `<text x="${tx}" y="${ty}" text-anchor="${anchor}" font-family="IBM Plex Mono, monospace" font-size="10" fill="${MUTED}">${esc(L.t)}</text>`;
  };
  if (labelPos === 'left') {
    // Label to the left of the icon, right-aligned, vertically centred.
    const lineH = 13;
    const center = iconY + ICON / 2;
    let ty = center - ((lines.length - 1) * lineH) / 2 + 4;
    const tx = iconX - 8;
    lines.forEach((L) => {
      s += drawLine(L, tx, ty, 'end');
      ty += lineH;
    });
  } else {
    let ty = y + ICON + 14;
    lines.forEach((L) => {
      s += drawLine(L, cx, ty, 'middle');
      ty += L.kind === 'sub' ? 12.5 : 13.5;
    });
  }
  return s;
}

// Port-name chip. `align` controls which edge sits at `x`:
//  - 'left'  : the chip's LEFT edge is at x (chip extends to the right)
//  - 'right' : the chip's RIGHT edge is at x (chip extends to the left)
//  - 'center': centred on x (default)
// This lets edgeSvg anchor the chip's INNER edge just outside the device box so
// it never gets covered, regardless of the (variable) chip width.
function portTag(x, y, text, align = 'center') {
  const w = Math.max(18, text.length * 6 + 8);
  let left;
  if (align === 'left') left = x;
  else if (align === 'right') left = x - w;
  else left = x - w / 2;
  const cx = left + w / 2;
  return (
    `<rect x="${left}" y="${y - 8}" width="${w}" height="14" rx="2" fill="#ffffff" opacity="0.92"/>` +
    `<text x="${cx}" y="${y + 2}" text-anchor="middle" font-family="IBM Plex Mono, monospace" font-size="9" fill="#2A3138">${esc(trunc(text, 12))}</text>`
  );
}

function portNameOf(node, portId) {
  if (!portId) return '';
  const pts = node?.data?.ports || [];
  const p = pts.find((x) => x.id === portId);
  return p ? p.name : '';
}

// Accent count pill for a bundled device-side anchor (mirrors the live canvas).
function countTag(x, y, n, align = 'center') {
  const text = String(n);
  const w = Math.max(18, text.length * 7 + 10);
  let left;
  if (align === 'left') left = x;
  else if (align === 'right') left = x - w;
  else left = x - w / 2;
  const cx = left + w / 2;
  return (
    `<rect x="${left}" y="${y - 8}" width="${w}" height="15" rx="7" fill="#ffffff" stroke="#0B5FFF" stroke-opacity="0.45" stroke-width="1"/>` +
    `<text x="${cx}" y="${y + 2.6}" text-anchor="middle" font-family="IBM Plex Mono, monospace" font-size="10" font-weight="700" fill="#0B5FFF">${esc(text)}</text>`
  );
}

// Handle anchor on the device card for a docking side. The card drawn by
// nodeSvg spans x..x+NODE_W horizontally and (y-2)..(y+62) vertically, so the
// anchors sit exactly on its edges (mirrors the live DeviceNode handles).
function sideAnchor(node, side) {
  const x = Math.round(node.position?.x || 0);
  const y = Math.round(node.position?.y || 0);
  const cx = x + NODE_W / 2;
  const cy = y + 2 + ICON / 2;
  if (side === 'l') return { x, y: cy };
  if (side === 'r') return { x: x + NODE_W, y: cy };
  if (side === 't') return { x: cx, y: y - 2 };
  return { x: cx, y: y + 2 + NODE_W - 4 }; // 'b'
}

// Places a port-name / count chip just outside the device card along the
// cable's first segment. (ax, ay) is the (fan-out shifted) handle anchor.
// Vertical sides rotate the chip -90deg so it reads along the cable, like the
// live canvas.
function sideChip(ax, ay, side, text, kind, d = 8) {
  // d = gap between card edge and chip (default 8; larger to clear a label)
  const tag = kind === 'count' ? countTag : portTag;
  let inner;
  if (side === 'r' || side === 't') inner = tag(d, -7, text, 'left');
  else inner = tag(-d, -7, text, 'right');
  const rot = side === 't' || side === 'b' ? ' rotate(-90)' : '';
  return `<g transform="translate(${ax},${ay})${rot}">${inner}</g>`;
}

// Side-aware orthogonal cable, geometry identical to the live canvas
// (ConnectionEdge + React Flow getSmoothStepPath): docks on the stored/auto
// resolved sides, 20px stubs, rounded bends, lane stagger for parallel cables
// sharing the same endpoints, and side-bundle collapse for busy device sides.
// Chip distance from the card edge for a given side. When the cable leaves
// through the device's label block (bottom exit with labels below, left exit
// with labels left) the chip is pushed past the label — but never beyond the
// cable's first straight segment (then it stays close and overlaps instead).
function chipDistance(node, side, pts, text, kind, ctx) {
  let d = 8;
  const crossesLabel = (side === 'b' && ctx.labelPos !== 'left') || (side === 'l' && ctx.labelPos === 'left');
  if (crossesLabel) d = Math.max(d, labelBlockExtent(node, ctx.doc, ctx.lang, ctx.labelPos) + 4);
  if (d > 8 && pts && pts.length > 2) {
    // first straight segment length from the anchor (pts[0]) along the side
    let i = 1;
    const vertical = side === 't' || side === 'b';
    while (i + 1 < pts.length && (vertical ? pts[i + 1].x === pts[0].x : pts[i + 1].y === pts[0].y)) i++;
    const segLen = vertical ? Math.abs(pts[i].y - pts[0].y) : Math.abs(pts[i].x - pts[0].x);
    const chipW = kind === 'count' ? Math.max(18, String(text).length * 7 + 10) : Math.max(18, String(text).length * 6 + 8);
    d = Math.max(8, Math.min(d, segLen - chipW - 6));
  }
  return d;
}

function edgeSvg(edge, nodeIndex, idx = 0, count = 1, showPortLabels = true, mirrored = false, bundle = {}, ctx = {}) {
  const a = nodeIndex[edge.source];
  const b = nodeIndex[edge.target];
  if (!a || !b) return '';
  const sSide = ['l', 'r', 't', 'b'].includes(edge.sourceSide) ? edge.sourceSide : 'r';
  const tSide = ['l', 'r', 't', 'b'].includes(edge.targetSide) ? edge.targetSide : 'l';
  const src = sideAnchor(a, sSide);
  const tgt = sideAnchor(b, tSide);
  const horizontal = sSide === 'l' || sSide === 'r';
  // Fan-out of parallel cables sharing the same endpoints (gap 14 like live).
  // A BUNDLED end (busy device side) uses offset 0 so its cables converge.
  const spread = count > 1 ? idx - (count - 1) / 2 : 0;
  const off = spread * 14;
  const sOff = bundle.sourceCollapsed ? 0 : off;
  const tOff = bundle.targetCollapsed ? 0 : off;
  const midOff = bundle.sourceCollapsed || bundle.targetCollapsed ? 0 : off;
  const sPt = { x: src.x + (horizontal ? 0 : sOff), y: src.y + (horizontal ? sOff : 0) };
  const tPt = { x: tgt.x + (horizontal ? 0 : tOff), y: tgt.y + (horizontal ? tOff : 0) };
  const center = count > 1 ? laneCenter(src, sSide, tgt, tSide, midOff) : {};
  const pts = smoothStepPoints(sPt, sSide, tPt, tSide, center);
  let s = `<path d="${pointsToRoundedPath(pts)}" fill="none" ${connSvgAttrs(edge.connType)} stroke-linejoin="round" stroke-linecap="round"/>`;
  const rpts = pts.slice().reverse(); // target-first for the target chip distance
  // Count label for a bundled anchor (drawn once per side by the label owner).
  if (bundle.sourceLabelOwner) {
    const n = String(bundle.sourceCount);
    s += sideChip(sPt.x, sPt.y, sSide, n, 'count', chipDistance(a, sSide, pts, n, 'count', ctx));
  }
  if (bundle.targetLabelOwner) {
    const n = String(bundle.targetCount);
    s += sideChip(tPt.x, tPt.y, tSide, n, 'count', chipDistance(b, tSide, rpts, n, 'count', ctx));
  }
  if (!showPortLabels) return mirrored ? `<g opacity="0.45">${s}</g>` : s;
  const sName = portNameOf(a, edge.sourcePortId);
  const tName = portNameOf(b, edge.targetPortId);
  // Suppressed at a bundled end (the count label replaces the per-cable names).
  if (sName && !bundle.sourceCollapsed) s += sideChip(sPt.x, sPt.y, sSide, sName, 'port', chipDistance(a, sSide, pts, sName, 'port', ctx));
  if (tName && !bundle.targetCollapsed) s += sideChip(tPt.x, tPt.y, tSide, tName, 'port', chipDistance(b, tSide, rpts, tName, 'port', ctx));
  return mirrored ? `<g opacity="0.45">${s}</g>` : s;
}

export function renderPageSVG(page, doc, lang, pageInfo, opts = {}) {
  const showPortLabels = opts.showPortLabels !== false;
  // Resolve alias placements (clones) to their master device's data so they
  // render with the real icon/label/VLAN/IP/ports. Position stays the alias's.
  const resolve = buildNodeResolver(doc);
  const rnodes = (page.nodes || []).map((n) =>
    n.data?.aliasOf ? { ...n, data: { ...(resolve(n.id)?.data || {}), aliasOf: n.data.aliasOf } } : n
  );
  const nodeIndex = {};
  rnodes.forEach((n) => (nodeIndex[n.id] = n));
  // Connections are ONE global topology: show every edge whose both endpoints
  // are present on this page (master or clone), remapped to local node ids.
  // Edges whose home page differs are drawn subtly (mirrored).
  const displayEdges = displayEdgesForPage(doc, page);
  // Fan out only cables sharing the SAME endpoints (device + docking side),
  // exactly like the live canvas (computeParallelLanes).
  const lanes = computeParallelLanes(displayEdges);
  // Bundle busy device sides (>3 cables on one side) into a single anchor +
  // count label — mirrors the live canvas.
  const bundles = computeSideBundles(displayEdges);
  let body = '';
  displayEdges.forEach((e) => {
    const lane = lanes[e.id] || { index: 0, count: 1 };
    body += edgeSvg(e, nodeIndex, lane.index, lane.count, showPortLabels, !!e.__mirrored, bundles[e.id] || {}, { doc, lang, labelPos: page.labelPos });
  });
  rnodes.forEach((n) => (body += nodeSvg(n, doc, lang, page.labelPos)));
  const s = page.contentScale || 1;
  const content = s !== 1 ? `<g transform="scale(${s})">${body}</g>` : body;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${SHEET.w}" height="${SHEET.h}" viewBox="0 0 ${SHEET.w} ${SHEET.h}">` +
    cadFrame(doc, lang, pageInfo, pageDisplayName(page.name, lang), usedConnTypes(doc)) +
    content +
    `</svg>`
  );
}

// ---- Logical diagram (devices grouped by VLAN) -----------------------------
// MASTER devices only — alias placements (clones on other pages) are skipped so
// a device placed on several pages is aggregated exactly once.
function allDevices(doc) {
  const list = [];
  (doc.pages || []).forEach((p) =>
    (p.nodes || []).forEach((n) => {
      if (n.data?.aliasOf) return;
      list.push({ ...n, _page: p.name });
    })
  );
  return list;
}

// Layout constants shared by the logical-diagram planner + renderer.
const LOG = { spineX: INNER + 26, boxX: INNER + 72, cellW: 156, cellH: 104, headerH: 40, gap: 22 };
const CONTENT_TOP = INNER + 70; // first usable y below the sheet heading
// Bottom limit for full-width auto-page content: stay clear of the title block
// (TB height 168) anchored bottom-right, plus a small safety margin.
const CONTENT_BOTTOM = SHEET.h - INNER - 168 - 12;

function logicalLayout() {
  const innerW = SHEET.w - 2 * INNER;
  const boxW = innerW - 72 - 16;
  const perRow = Math.max(1, Math.floor((boxW - 24) / LOG.cellW));
  return { boxW, perRow };
}

function logicalGroups(doc) {
  const devices = allDevices(doc);
  const groups = [];
  doc.vlans
    .slice()
    .sort((a, b) => a.vlanId - b.vlanId)
    .forEach((v) => {
      const items = devices.filter((d) => d.data?.vlanRef === v.id);
      if (items.length) groups.push({ vlan: v, items });
    });
  const unassigned = devices.filter((d) => !doc.vlans.some((v) => v.id === d.data?.vlanRef));
  if (unassigned.length) groups.push({ vlan: null, items: unassigned });
  return groups;
}

// Split the VLAN groups across A3 sheets. Returns an array of pages; each page is
// { chunks: [{ vlan, items, cont }] } (cont = continuation of a split group). An
// empty document yields a single { empty: true } page.
export function planLogicalPages(doc, lang) {
  const devices = allDevices(doc);
  if (!devices.length) return [{ empty: true, chunks: [] }];
  const { perRow } = logicalLayout();
  const { headerH, cellH, gap } = LOG;
  const groups = logicalGroups(doc);
  const maxRows = Math.max(1, Math.floor((CONTENT_BOTTOM - CONTENT_TOP - headerH - 14) / cellH));

  const pages = [];
  let cur = [];
  let curY = CONTENT_TOP;
  const flush = () => {
    if (cur.length) {
      pages.push({ chunks: cur });
      cur = [];
      curY = CONTENT_TOP;
    }
  };

  groups.forEach((g) => {
    let items = g.items.slice();
    let cont = false;
    while (items.length) {
      let rowsFit = Math.floor((CONTENT_BOTTOM - curY - headerH - 14) / cellH);
      if (rowsFit < 1) {
        if (cur.length) {
          flush();
          continue;
        }
        rowsFit = maxRows; // empty page: place at least one row block
      }
      rowsFit = Math.min(rowsFit, maxRows);
      const take = Math.min(items.length, rowsFit * perRow);
      const chunk = items.slice(0, take);
      items = items.slice(take);
      const rows = Math.ceil(chunk.length / perRow);
      const boxH = headerH + rows * cellH + 14;
      cur.push({ vlan: g.vlan, items: chunk, cont });
      curY += boxH + gap;
      cont = true;
    }
  });
  flush();
  if (!pages.length) pages.push({ empty: true, chunks: [] });
  return pages;
}

// Render exactly one logical-diagram sheet from a planned page.
export function renderLogicalPage(page, doc, lang, pageInfo = { index: '\u2014', total: '\u2014' }) {
  const heading = lang === 'en' ? 'Logical Network Diagram' : 'Logisches Netzwerkdiagramm';
  const legend = usedConnTypes(doc);
  const frame = cadFrame(doc, lang, pageInfo, heading, legend);

  if (!page || page.empty) {
    const msg = `<text x="${SHEET.w / 2}" y="${SHEET.h / 2}" text-anchor="middle" font-family="IBM Plex Sans, sans-serif" font-size="18" fill="${MUTED}">${esc(lang === 'en' ? 'No devices yet.' : 'Noch keine Ger\u00e4te vorhanden.')}</text>`;
    return wrap(frame + msg);
  }

  const { boxW, perRow } = logicalLayout();
  const { spineX, boxX, cellW, cellH, headerH, gap } = LOG;
  const contLabel = lang === 'en' ? ' (cont.)' : ' (Fortsetzung)';
  let body = '';
  let y = CONTENT_TOP;
  const spineTop = y;
  let spineBottom = y;

  page.chunks.forEach((g) => {
    const rows = Math.ceil(g.items.length / perRow);
    const boxH = headerH + rows * cellH + 14;
    const color = g.vlan ? g.vlan.color : MUTED;
    let title = g.vlan ? `VLAN ${g.vlan.vlanId} \u00b7 ${g.vlan.name}` : lang === 'en' ? 'Unassigned' : 'Ohne VLAN';
    if (g.cont) title += contLabel;
    const net = g.vlan && g.vlan.network ? g.vlan.network : '';

    const midY = y + boxH / 2;
    body += `<line x1="${spineX}" y1="${midY}" x2="${boxX}" y2="${midY}" stroke="${FRAME}" stroke-width="1.2"/>`;
    body += `<circle cx="${spineX}" cy="${midY}" r="3.5" fill="${FRAME}"/>`;

    body += `<rect x="${boxX}" y="${y}" width="${boxW}" height="${boxH}" rx="10" fill="#ffffff" stroke="${FRAME}" stroke-width="1.2"/>`;
    body += `<rect x="${boxX}" y="${y}" width="${boxW}" height="${headerH}" rx="10" fill="${color}" fill-opacity="0.08"/>`;
    body += `<circle cx="${boxX + 18}" cy="${y + headerH / 2}" r="6" fill="${color}"/>`;
    body += `<text x="${boxX + 34}" y="${y + 26}" font-family="IBM Plex Sans, sans-serif" font-size="14" font-weight="600" fill="${INK}">${esc(title)}</text>`;
    if (net) body += `<text x="${boxX + boxW - 14}" y="${y + 26}" text-anchor="end" font-family="IBM Plex Mono, monospace" font-size="12" fill="${MUTED}">${esc(net)}</text>`;

    g.items.forEach((d, i) => {
      const col = i % perRow;
      const row = Math.floor(i / perRow);
      const cx = boxX + 14 + col * cellW + cellW / 2;
      const iy = y + headerH + row * cellH + 8;
      const iconX = cx - 22;
      body += iconAt(d.data?.deviceType, iconX, iy, 44);
      const name = d.data?.label || deviceLabel(d.data?.deviceType, lang);
      body += `<text x="${cx}" y="${iy + 58}" text-anchor="middle" font-family="IBM Plex Sans, sans-serif" font-size="11.5" font-weight="600" fill="${INK}">${esc(trunc(name, 16))}</text>`;
      if (d.data?.ip) body += `<text x="${cx}" y="${iy + 72}" text-anchor="middle" font-family="IBM Plex Mono, monospace" font-size="9.5" fill="${MUTED}">${esc(trunc(d.data.ip, 18))}</text>`;
    });

    spineBottom = y + boxH / 2;
    y += boxH + gap;
  });

  body =
    `<line x1="${spineX}" y1="${spineTop - 6}" x2="${spineX}" y2="${spineBottom}" stroke="${FRAME}" stroke-width="2"/>` +
    `<rect x="${spineX - 34}" y="${spineTop - 40}" width="68" height="26" rx="6" fill="${FRAME}"/>` +
    `<text x="${spineX}" y="${spineTop - 22}" text-anchor="middle" font-family="IBM Plex Sans, sans-serif" font-size="11" font-weight="600" fill="#ffffff">L3 / Core</text>` +
    body;

  return wrap(frame + body);
}

// Render every logical sheet (used by AutoView + the export builder).
export function renderLogicalSVGPages(doc, lang, opts = {}) {
  const startIndex = opts.startIndex || 1;
  const total = opts.total || 1;
  return planLogicalPages(doc, lang).map((pg, i) => renderLogicalPage(pg, doc, lang, { index: startIndex + i, total }));
}

// Back-compat single-sheet renderer (first sheet only).
export function renderLogicalSVG(doc, lang, pageInfo = { index: '\u2014', total: '\u2014' }) {
  return renderLogicalPage(planLogicalPages(doc, lang)[0], doc, lang, pageInfo);
}

// ---- Cable list ------------------------------------------------------------
// Lists every connection (cable) between devices with its endpoints
// (device · port) and medium, grouped by cable medium (connType). Replaces the
// former "logical diagram" which the VLAN list already covers better.
const CL = { headerH: 40, colHeader: 22, rowH: 24, sectionGap: 16 };

function cableEndpointName(node, portId, lang) {
  const name = node.data?.label || deviceLabel(node.data?.deviceType, lang);
  const port = portNameOf(node, portId);
  return port ? `${name}  \u00b7  ${port}` : name;
}

function cableSections(doc, lang) {
  const nodeById = {};
  const lagById = {};
  (doc.pages || []).forEach((p) => {
    (p.nodes || []).forEach((n) => {
      if (n.data?.aliasOf) return; // index masters first
      nodeById[n.id] = n;
    });
    (p.lags || []).forEach((l) => (lagById[l.id] = l));
  });
  // Map every alias placement id onto its master node so connections drawn on a
  // clone's page resolve to the real device (name / ports / VLANs).
  (doc.pages || []).forEach((p) =>
    (p.nodes || []).forEach((n) => {
      if (n.data?.aliasOf && nodeById[n.data.aliasOf]) nodeById[n.id] = nodeById[n.data.aliasOf];
    })
  );
  const resolvedCache = {};
  const resolveFor = (id) => {
    if (!resolvedCache[id]) resolvedCache[id] = nodeById[id] ? resolvePortVlans(nodeById[id], doc) : {};
    return resolvedCache[id];
  };
  const byType = {};
  (doc.pages || []).forEach((p) => {
    if (p.type === 'detail' || p.type === 'location') return;
    (p.edges || []).forEach((e) => {
      const a = nodeById[e.source];
      const b = nodeById[e.target];
      if (!a || !b) return;
      const ra = resolveFor(e.source)[e.sourcePortId] || {};
      const rb = resolveFor(e.target)[e.targetPortId] || {};
      // --- Untagged (Native) VLAN: pro Einzelverbindung (auch innerhalb LAGs).
      // Konflikt nur, wenn BEIDE Enden ein Untagged-VLAN tragen UND es abweicht
      // (deckt sich mit dem Design-Check `untagged_vlan_mismatch`). Ist nur eine
      // Seite konfiguriert, gilt deren ID; sind beide leer -> "\u2014".
      const uaId = ra.untaggedVlan ? ra.untaggedVlan.vlanId : null;
      const ubId = rb.untaggedVlan ? rb.untaggedVlan.vlanId : null;
      const untaggedConflict = uaId != null && ubId != null && uaId !== ubId;
      let untaggedVal = '';
      if (!untaggedConflict) {
        const id = uaId != null ? uaId : ubId;
        if (id != null) untaggedVal = String(id);
      }
      // --- Tagged VLANs: Konflikt nur, wenn BEIDE Enden getaggte VLANs tragen
      // UND die Mengen abweichen. Sonst gilt die konfigurierte Seite.
      const taggedA = (ra.taggedVlans || []).map((v) => v.vlanId).sort((x, y) => x - y);
      const taggedB = (rb.taggedVlans || []).map((v) => v.vlanId).sort((x, y) => x - y);
      const taggedConflict = taggedA.length > 0 && taggedB.length > 0 && taggedA.join(',') !== taggedB.join(',');
      let taggedVal = '';
      if (!taggedConflict) {
        const set = taggedA.length ? taggedA : taggedB;
        taggedVal = set.join(', ');
      }
      const key = e.connType || 'ethernet';
      (byType[key] = byType[key] || []).push({
        from: cableEndpointName(a, e.sourcePortId, lang),
        to: cableEndpointName(b, e.targetPortId, lang),
        name: e.name || '',
        lag: e.lagId ? lagById[e.lagId]?.name || 'LAG' : '',
        untaggedVal,
        untaggedConflict,
        taggedVal,
        taggedConflict,
        page: pageDisplayName(p.name, lang),
      });
    });
  });
  return Object.keys(byType)
    .sort((x, y) => connLabel(x, lang).localeCompare(connLabel(y, lang)))
    .map((k) => ({ connType: k, items: byType[k] }));
}

// Split cable sections across A3 sheets (row-wise, like the VLAN list).
export function planCableListPages(doc, lang) {
  const sections = cableSections(doc, lang);
  if (!sections.length) return [{ empty: true, chunks: [] }];
  const { headerH, colHeader, rowH, sectionGap } = CL;
  const headH = headerH + 4 + colHeader;
  const maxRows = Math.max(1, Math.floor((CONTENT_BOTTOM - CONTENT_TOP - headH) / rowH));

  const pages = [];
  let cur = [];
  let curY = CONTENT_TOP;
  const flush = () => {
    if (cur.length) {
      pages.push({ chunks: cur });
      cur = [];
      curY = CONTENT_TOP;
    }
  };

  sections.forEach((sec) => {
    let items = sec.items.slice();
    let cont = false;
    while (items.length) {
      let rowsFit = Math.floor((CONTENT_BOTTOM - curY - headH) / rowH);
      if (rowsFit < 1) {
        if (cur.length) {
          flush();
          continue;
        }
        rowsFit = maxRows;
      }
      rowsFit = Math.min(rowsFit, maxRows);
      const take = Math.min(items.length, rowsFit);
      cur.push({ connType: sec.connType, items: items.slice(0, take), cont });
      items = items.slice(take);
      curY += headH + take * rowH + sectionGap;
      cont = true;
    }
  });
  flush();
  if (!pages.length) pages.push({ empty: true, chunks: [] });
  return pages;
}

// Render exactly one cable-list sheet from a planned page.
export function renderCableListPage(page, doc, lang, pageInfo = { index: '\u2014', total: '\u2014' }) {
  const heading = lang === 'en' ? 'Cable List' : 'Kabelliste';
  const frame = cadFrame(doc, lang, pageInfo, heading);
  if (!page || page.empty) {
    const msg = `<text x="${SHEET.w / 2}" y="${SHEET.h / 2}" text-anchor="middle" font-family="IBM Plex Sans, sans-serif" font-size="18" fill="${MUTED}">${esc(lang === 'en' ? 'No connections yet.' : 'Noch keine Verbindungen.')}</text>`;
    return wrap(frame + msg);
  }

  const innerW = SHEET.w - 2 * INNER;
  const x0 = INNER + 16;
  const tableW = innerW - 32;
  const contLabel = lang === 'en' ? ' (cont.)' : ' (Fortsetzung)';
  const cFrom = x0 + 18;
  const cTo = x0 + tableW * 0.2;
  const cName = x0 + tableW * 0.4;
  const cUnt = x0 + tableW * 0.56;
  const cTag = x0 + tableW * 0.67;
  const cLag = x0 + tableW * 0.82;
  const cPage = x0 + tableW * 0.92;
  let y = CONTENT_TOP;
  let body = '';

  // Render one VLAN cell: a numeric value, an "\u2014" placeholder, or a warning
  // triangle + "Konflikt" text when the two endpoints disagree.
  const vlanCell = (xc, ry, val, conflict, maxChars) => {
    if (conflict) {
      return (
        warnTri(xc, ry + 2, 12) +
        `<text x="${xc + 17}" y="${ry + 14}" font-family="IBM Plex Sans, sans-serif" font-size="11" font-weight="600" fill="${WARN}">${esc(lang === 'en' ? 'Conflict' : 'Konflikt')}</text>`
      );
    }
    const txt = val ? trunc(val, maxChars) : '\u2014';
    return `<text x="${xc}" y="${ry + 14}" font-family="IBM Plex Mono, monospace" font-size="12" fill="${val ? INK : MUTED}">${esc(txt)}</text>`;
  };

  page.chunks.forEach((sec) => {
    const ct = getConnType(sec.connType);
    const color = (ct && ct.color) || MUTED;
    const headerH = 40;
    let title = connLabel(sec.connType, lang);
    if (sec.cont) title += contLabel;
    body += `<rect x="${x0}" y="${y}" width="${tableW}" height="${headerH}" rx="8" fill="${color}" fill-opacity="0.08" stroke="${FRAME}" stroke-width="1"/>`;
    body += `<line x1="${x0 + 14}" y1="${y + headerH / 2}" x2="${x0 + 42}" y2="${y + headerH / 2}" ${connSvgAttrs(sec.connType)} stroke-linecap="round"/>`;
    body += `<text x="${x0 + 54}" y="${y + headerH / 2 + 5}" font-family="IBM Plex Sans, sans-serif" font-size="14" font-weight="600" fill="${INK}">${esc(title)}</text>`;
    body += `<text x="${x0 + tableW - 14}" y="${y + headerH / 2 + 5}" text-anchor="end" font-family="IBM Plex Mono, monospace" font-size="12" fill="${MUTED}">${sec.items.length}\u00d7</text>`;
    y += headerH + 4;

    body += `<text x="${cFrom}" y="${y + 13}" font-family="IBM Plex Sans, sans-serif" font-size="10" letter-spacing="0.5" fill="${MUTED}">${esc(lang === 'en' ? 'FROM (DEVICE \u00b7 PORT)' : 'VON (GER\u00c4T \u00b7 PORT)')}</text>`;
    body += `<text x="${cTo}" y="${y + 13}" font-family="IBM Plex Sans, sans-serif" font-size="10" letter-spacing="0.5" fill="${MUTED}">${esc(lang === 'en' ? 'TO (DEVICE \u00b7 PORT)' : 'NACH (GER\u00c4T \u00b7 PORT)')}</text>`;
    body += `<text x="${cName}" y="${y + 13}" font-family="IBM Plex Sans, sans-serif" font-size="10" letter-spacing="0.5" fill="${MUTED}">${esc(lang === 'en' ? 'LABEL' : 'BEZEICHNUNG')}</text>`;
    body += `<text x="${cUnt}" y="${y + 13}" font-family="IBM Plex Sans, sans-serif" font-size="10" letter-spacing="0.5" fill="${MUTED}">UNTAGGED</text>`;
    body += `<text x="${cTag}" y="${y + 13}" font-family="IBM Plex Sans, sans-serif" font-size="10" letter-spacing="0.5" fill="${MUTED}">TAGGED</text>`;
    body += `<text x="${cLag}" y="${y + 13}" font-family="IBM Plex Sans, sans-serif" font-size="10" letter-spacing="0.5" fill="${MUTED}">LAG</text>`;
    body += `<text x="${cPage}" y="${y + 13}" font-family="IBM Plex Sans, sans-serif" font-size="10" letter-spacing="0.5" fill="${MUTED}">${esc(lang === 'en' ? 'PAGE' : 'SEITE')}</text>`;
    y += 22;
    body += `<line x1="${x0}" y1="${y - 4}" x2="${x0 + tableW}" y2="${y - 4}" stroke="${FRAME}" stroke-opacity="0.3" stroke-width="1"/>`;

    sec.items.forEach((it, i) => {
      const ry = y + i * 24;
      if (i % 2 === 1) body += `<rect x="${x0}" y="${ry - 2}" width="${tableW}" height="24" fill="${INK}" fill-opacity="0.025"/>`;
      body += `<text x="${cFrom}" y="${ry + 14}" font-family="IBM Plex Sans, sans-serif" font-size="12" fill="${INK}">${esc(trunc(it.from, 26))}</text>`;
      body += `<text x="${cTo - 16}" y="${ry + 14}" text-anchor="middle" font-family="IBM Plex Sans, sans-serif" font-size="12" fill="${MUTED}">\u2192</text>`;
      body += `<text x="${cTo}" y="${ry + 14}" font-family="IBM Plex Sans, sans-serif" font-size="12" fill="${INK}">${esc(trunc(it.to, 26))}</text>`;
      body += `<text x="${cName}" y="${ry + 14}" font-family="IBM Plex Sans, sans-serif" font-size="12" fill="${INK}">${esc(trunc(it.name || '\u2014', 22))}</text>`;
      body += vlanCell(cUnt, ry, it.untaggedVal, it.untaggedConflict, 10);
      body += vlanCell(cTag, ry, it.taggedVal, it.taggedConflict, 18);
      if (it.lag) {
        const lw = Math.min(96, 12 + it.lag.length * 6.4);
        body += `<rect x="${cLag}" y="${ry}" width="${lw}" height="17" rx="8.5" fill="#0B5FFF" fill-opacity="0.1"/>`;
        body += `<text x="${cLag + lw / 2}" y="${ry + 13}" text-anchor="middle" font-family="IBM Plex Sans, sans-serif" font-size="10" font-weight="600" fill="#0B5FFF">${esc(trunc(it.lag, 12))}</text>`;
      } else {
        body += `<text x="${cLag}" y="${ry + 14}" font-family="IBM Plex Sans, sans-serif" font-size="12" fill="${MUTED}">\u2014</text>`;
      }
      body += `<text x="${cPage}" y="${ry + 14}" font-family="IBM Plex Sans, sans-serif" font-size="11" fill="${MUTED}">${esc(trunc(it.page, 12))}</text>`;
    });
    y += sec.items.length * 24 + 6 + CL.sectionGap;
  });

  return wrap(frame + body);
}

// Render every cable-list sheet (used by AutoView + the export builder).
export function renderCableListSVGPages(doc, lang, opts = {}) {
  const startIndex = opts.startIndex || 1;
  const total = opts.total || 1;
  return planCableListPages(doc, lang).map((pg, i) => renderCableListPage(pg, doc, lang, { index: startIndex + i, total }));
}


// ---- VLAN list -------------------------------------------------------------
const VL = { headerBlock: 56, colHeader: 20, rowH: 24, sectionGap: 22, zoneHead: 30 };

// Numeric IPv4 sort value (o1.o2.o3.o4 -> integer). Returns null when no IPv4
// address can be parsed, so such devices sort to the end (then by name).
export function ipSortVal(ip) {
  if (!ip) return null;
  const m = String(ip).match(/(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})/);
  if (!m) return null;
  return ((+m[1] * 256 + +m[2]) * 256 + +m[3]) * 256 + +m[4];
}

// Sort devices by IP address (ascending). Devices without a parseable IP are
// placed last and ordered by name. Used by the VLAN list (both export paths).
export function sortDevicesByIp(devices) {
  return devices.slice().sort((a, b) => {
    const ia = ipSortVal(a.data?.ip);
    const ib = ipSortVal(b.data?.ip);
    if (ia == null && ib == null) return (a.data?.label || '').localeCompare(b.data?.label || '');
    if (ia == null) return 1;
    if (ib == null) return -1;
    return ia - ib;
  });
}

function vlanSections(doc, lang = 'de') {
  const entries = deviceIpEntries(doc, lang);
  const zonesOn = !!doc.zonesEnabled;
  const sections = [];
  const byVlanId = (a, b) => a.vlanId - b.vlanId;
  const mkSection = (v, zoneHeader) => ({ vlan: v, items: sortEntriesByIp(entries.filter((e) => e.vlanRef === v.id)), zoneHeader });

  if (zonesOn) {
    // Group VLANs by zone (in the zone list order); the first VLAN of each zone
    // carries a `zoneHeader` marker so the renderer draws a zone band above it.
    const zones = doc.zones || [];
    zones.forEach((z) => {
      const zvlans = doc.vlans.filter((v) => v.zoneId === z.id).sort(byVlanId);
      if (!zvlans.length) return; // skip empty zones in the export
      zvlans.forEach((v, i) => sections.push(mkSection(v, i === 0 ? { name: z.name || '' } : undefined)));
    });
    const noZone = doc.vlans
      .filter((v) => !v.zoneId || !zones.some((z) => z.id === v.zoneId))
      .sort(byVlanId);
    noZone.forEach((v, i) => sections.push(mkSection(v, i === 0 ? { none: true } : undefined)));
  } else {
    doc.vlans.slice().sort(byVlanId).forEach((v) => sections.push(mkSection(v, undefined)));
  }

  const unassigned = sortEntriesByIp(entries.filter((e) => !doc.vlans.some((v) => v.id === e.vlanRef)));
  if (unassigned.length) sections.push({ vlan: null, items: unassigned, zoneHeader: undefined });
  return sections;
}

// Collect every IP-bearing configuration point as its OWN row for the VLAN
// list: the device itself PLUS each physical port and each virtual interface
// that carries an IP. Alias placements are skipped (allDevices dedupes), so a
// device on several pages is listed once. The `port` field is empty when the
// address/VLAN is configured directly on the device.
export function deviceIpEntries(doc, lang = 'de') {
  const entries = [];
  allDevices(doc).forEach((n) => {
    const d = n.data || {};
    const label = d.label || deviceLabel(d.deviceType, lang);
    // Device-level row — always present so a device stays listed in its VLAN.
    const pageNm = pageDisplayName(n._page, lang);
    entries.push({ label, deviceType: d.deviceType, page: pageNm, port: '', ip: d.ip || '', vlanRef: d.vlanRef || null, description: d.description || '' });
    // Physical ports that carry an IP.
    (d.ports || []).forEach((p) => {
      if (p.ip) entries.push({ label, deviceType: d.deviceType, page: pageNm, port: p.name || '', ip: p.ip, vlanRef: p.untaggedVlanRef || null, description: '' });
    });
    // Virtual interfaces that carry an IP (untagged VLAN uses the interface IP).
    (d.interfaces || []).forEach((it) => {
      if (it.ip) entries.push({ label, deviceType: d.deviceType, page: pageNm, port: it.name || '', ip: it.ip, vlanRef: it.untaggedVlanRef || null, description: '' });
      // Per-tagged-VLAN IPs (each is its own row under its VLAN).
      if (it.taggedIps) {
        Object.keys(it.taggedIps).forEach((vref) => {
          const ip = it.taggedIps[vref];
          if (ip) entries.push({ label, deviceType: d.deviceType, page: pageNm, port: it.name || '', ip, vlanRef: vref, description: '' });
        });
      }
    });
  });
  return entries;
}

export function sortEntriesByIp(entries) {
  return entries.slice().sort((a, b) => {
    const ia = ipSortVal(a.ip);
    const ib = ipSortVal(b.ip);
    if (ia == null && ib == null) return (a.label || '').localeCompare(b.label || '') || (a.port || '').localeCompare(b.port || '');
    if (ia == null) return 1;
    if (ib == null) return -1;
    return ia - ib || (a.label || '').localeCompare(b.label || '');
  });
}

// Split the VLAN sections across A3 sheets. Returns an array of pages; each page
// is { chunks: [{ vlan, items, cont, empty }] }. Sections that overflow are split
// row-wise and continued on the next sheet (cont = true).
export function planVlanListPages(doc, lang) {
  const devices = allDevices(doc);
  if (!doc.vlans.length && !devices.length) return [{ empty: true, chunks: [] }];
  const sections = vlanSections(doc, lang);
  const { headerBlock, colHeader, rowH, sectionGap, zoneHead } = VL;
  const headH = headerBlock + colHeader;
  const maxRows = Math.max(1, Math.floor((CONTENT_BOTTOM - CONTENT_TOP - headH) / rowH));

  const pages = [];
  let cur = [];
  let curY = CONTENT_TOP;
  const flush = () => {
    if (cur.length) {
      pages.push({ chunks: cur });
      cur = [];
      curY = CONTENT_TOP;
    }
  };

  sections.forEach((sec) => {
    const zoneExtra = sec.zoneHeader ? zoneHead : 0; // only counts on the first (non-cont) chunk
    if (!sec.items.length) {
      // empty section: header + col-header + one placeholder row
      const rowsFit = Math.floor((CONTENT_BOTTOM - curY - headH - zoneExtra) / rowH);
      if (rowsFit < 1 && cur.length) flush();
      cur.push({ vlan: sec.vlan, items: [], cont: false, empty: true, zoneHeader: sec.zoneHeader });
      curY += headH + rowH + sectionGap + zoneExtra;
      return;
    }
    let items = sec.items.slice();
    let cont = false;
    while (items.length) {
      const extra = cont ? 0 : zoneExtra;
      let rowsFit = Math.floor((CONTENT_BOTTOM - curY - headH - extra) / rowH);
      if (rowsFit < 1) {
        if (cur.length) {
          flush();
          continue;
        }
        rowsFit = maxRows; // empty page: place at least one row
      }
      rowsFit = Math.min(rowsFit, maxRows);
      const take = Math.min(items.length, rowsFit);
      const chunk = items.slice(0, take);
      items = items.slice(take);
      cur.push({ vlan: sec.vlan, items: chunk, cont, empty: false, zoneHeader: cont ? undefined : sec.zoneHeader });
      curY += headH + chunk.length * rowH + sectionGap + extra;
      cont = true;
    }
  });
  flush();
  if (!pages.length) pages.push({ empty: true, chunks: [] });
  return pages;
}

// Render exactly one VLAN-list sheet from a planned page.
export function renderVlanListPage(page, doc, lang, pageInfo = { index: '\u2014', total: '\u2014' }) {
  const heading = lang === 'en' ? 'VLAN List' : 'VLAN-Liste';
  const frame = cadFrame(doc, lang, pageInfo, heading);
  if (!page || page.empty) {
    const msg = `<text x="${SHEET.w / 2}" y="${SHEET.h / 2}" text-anchor="middle" font-family="IBM Plex Sans, sans-serif" font-size="18" fill="${MUTED}">${esc(lang === 'en' ? 'No VLANs yet.' : 'Noch keine VLANs.')}</text>`;
    return wrap(frame + msg);
  }

  const innerW = SHEET.w - 2 * INNER;
  const x0 = INNER + 16;
  const tableW = innerW - 32;
  const contLabel = lang === 'en' ? ' (cont.)' : ' (Fortsetzung)';
  let y = CONTENT_TOP;
  let body = '';

  page.chunks.forEach((sec) => {
    // Zone band above the first VLAN of a zone group (zones feature only).
    if (sec.zoneHeader) {
      const zlabel = sec.zoneHeader.none ? (lang === 'en' ? 'No zone' : 'Ohne Zone') : (sec.zoneHeader.name || (lang === 'en' ? 'Zone' : 'Zone'));
      body += `<text x="${x0}" y="${y + 15}" font-family="IBM Plex Sans, sans-serif" font-size="12" font-weight="700" letter-spacing="1.5" fill="${INK}">${esc(((lang === 'en' ? 'ZONE' : 'ZONE') + '  \u00b7  ' + zlabel).toUpperCase())}</text>`;
      body += `<line x1="${x0}" y1="${y + 22}" x2="${x0 + tableW}" y2="${y + 22}" stroke="${FRAME}" stroke-width="1.5"/>`;
      y += VL.zoneHead;
    }
    const vlan = sec.vlan;
    const color = vlan ? vlan.color : MUTED;
    const info = vlan && vlan.network ? subnetInfo(vlan.network) : null;
    const headerH = 52;
    body += `<rect x="${x0}" y="${y}" width="${tableW}" height="${headerH}" rx="8" fill="${color}" fill-opacity="0.08" stroke="${FRAME}" stroke-width="1"/>`;
    body += `<circle cx="${x0 + 18}" cy="${y + 20}" r="6" fill="${color}"/>`;
    let title = vlan ? `VLAN ${vlan.vlanId} \u00b7 ${vlan.name}` : lang === 'en' ? 'Unassigned devices' : 'Ger\u00e4te ohne VLAN';
    if (sec.cont) title += contLabel;
    body += `<text x="${x0 + 34}" y="${y + 25}" font-family="IBM Plex Sans, sans-serif" font-size="14" font-weight="600" fill="${INK}">${esc(title)}</text>`;
    if (vlan) {
      const meta = [];
      if (vlan.network) meta.push(vlan.network);
      if (info && info.valid) meta.push((lang === 'en' ? 'usable ' : 'nutzbar ') + info.usable);
      if (info && info.valid) meta.push(info.firstHost + ' \u2013 ' + info.lastHost);
      if (vlan.dhcpStart && vlan.dhcpEnd) meta.push('DHCP ' + vlan.dhcpStart + ' \u2013 ' + vlan.dhcpEnd);
      body += `<text x="${x0 + 34}" y="${y + 43}" font-family="IBM Plex Mono, monospace" font-size="11" fill="${MUTED}">${esc(trunc(meta.join('   \u2502   '), 150))}</text>`;
    }
    y += headerH + 4;

    const cName = x0 + 18;
    const cPort = x0 + tableW * 0.22;
    const cDesc = x0 + tableW * 0.38;
    const cIp = x0 + tableW * 0.6;
    const cType = x0 + tableW * 0.78;
    body += `<text x="${cName}" y="${y + 13}" font-family="IBM Plex Sans, sans-serif" font-size="10" letter-spacing="0.5" fill="${MUTED}">${esc(lang === 'en' ? 'DEVICE' : 'GER\u00c4T')}</text>`;
    body += `<text x="${cPort}" y="${y + 13}" font-family="IBM Plex Sans, sans-serif" font-size="10" letter-spacing="0.5" fill="${MUTED}">${esc(lang === 'en' ? 'PORT / INTERFACE' : 'PORT / INTERFACE')}</text>`;
    body += `<text x="${cDesc}" y="${y + 13}" font-family="IBM Plex Sans, sans-serif" font-size="10" letter-spacing="0.5" fill="${MUTED}">${esc(lang === 'en' ? 'DESCRIPTION' : 'BESCHREIBUNG')}</text>`;
    body += `<text x="${cIp}" y="${y + 13}" font-family="IBM Plex Sans, sans-serif" font-size="10" letter-spacing="0.5" fill="${MUTED}">IP</text>`;
    body += `<text x="${cType}" y="${y + 13}" font-family="IBM Plex Sans, sans-serif" font-size="10" letter-spacing="0.5" fill="${MUTED}">${esc(lang === 'en' ? 'TYPE / PAGE' : 'TYP / SEITE')}</text>`;
    y += 20;
    body += `<line x1="${x0}" y1="${y - 4}" x2="${x0 + tableW}" y2="${y - 4}" stroke="${FRAME}" stroke-opacity="0.3" stroke-width="1"/>`;

    if (sec.empty || !sec.items.length) {
      body += `<text x="${cName}" y="${y + 14}" font-family="IBM Plex Mono, monospace" font-size="11" fill="${MUTED}">\u2014</text>`;
      y += 24;
    } else {
      sec.items.forEach((it, i) => {
        const ry = y + i * 24;
        if (i % 2 === 1) body += `<rect x="${x0}" y="${ry - 2}" width="${tableW}" height="24" fill="${INK}" fill-opacity="0.025"/>`;
        const name = it.label || deviceLabel(it.deviceType, lang);
        const desc = (it.description || '').replace(/\s*\n\s*/g, ' \u00b7 ').trim();
        body += `<text x="${cName}" y="${ry + 14}" font-family="IBM Plex Sans, sans-serif" font-size="12" fill="${INK}">${esc(trunc(name, 22))}</text>`;
        if (it.port) body += `<text x="${cPort}" y="${ry + 14}" font-family="IBM Plex Mono, monospace" font-size="11" fill="${MUTED}">${esc(trunc(it.port, 20))}</text>`;
        if (desc) body += `<text x="${cDesc}" y="${ry + 14}" font-family="IBM Plex Sans, sans-serif" font-size="11" fill="${MUTED}">${esc(trunc(desc, 44))}</text>`;
        body += `<text x="${cIp}" y="${ry + 14}" font-family="IBM Plex Mono, monospace" font-size="12" fill="${INK}">${esc(it.ip || '\u2014')}</text>`;
        body += `<text x="${cType}" y="${ry + 14}" font-family="IBM Plex Sans, sans-serif" font-size="11" fill="${MUTED}">${esc(deviceLabel(it.deviceType, lang) + '  \u00b7  ' + (it.page || ''))}</text>`;
      });
      y += sec.items.length * 24 + 6;
    }
    y += 16;
  });

  return wrap(frame + body);
}

// Render every VLAN-list sheet (used by AutoView + the export builder).
export function renderVlanListSVGPages(doc, lang, opts = {}) {
  const startIndex = opts.startIndex || 1;
  const total = opts.total || 1;
  return planVlanListPages(doc, lang).map((pg, i) => renderVlanListPage(pg, doc, lang, { index: startIndex + i, total }));
}

// Back-compat single-sheet renderer (first sheet only).
export function renderVlanListSVG(doc, lang, pageInfo = { index: '\u2014', total: '\u2014' }) {
  return renderVlanListPage(planVlanListPages(doc, lang)[0], doc, lang, pageInfo);
}

function wrap(inner) {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${SHEET.w}" height="${SHEET.h}" viewBox="0 0 ${SHEET.w} ${SHEET.h}">${inner}</svg>`;
}

// ---- Detail page (devices with ALL ports, port-to-port connections) --------
function detailLegendTypes(model) {
  const set = new Set();
  model.internalEdges.forEach((e) => set.add(e.connType || 'ethernet'));
  model.stubs.forEach((s) => set.add(s.connType || 'ethernet'));
  return Array.from(set);
}

function detailDeviceSvg(d, doc, lang, opts = {}) {
  const showBadges = opts.showBadges !== false;
  const x = Math.round(d.position.x);
  const y = Math.round(d.position.y);
  const w = DLAYOUT.BOX_W;
  const rowH = detailRowHeight(d.ports.length);
  const font = detailRowFont(rowH);
  const badgeFont = Math.max(7, font - 2);
  const h = detailBoxHeight(d.ports.length, rowH);
  const vlan = doc.vlans.find((v) => v.id === d.node.data?.vlanRef);
  const name = d.node.data?.label || deviceLabel(d.node.data?.deviceType, lang);
  let s = `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="10" fill="#ffffff" stroke="${FRAME}" stroke-width="1.3"/>`;
  s += `<line x1="${x}" y1="${y + DLAYOUT.HEADER_H}" x2="${x + w}" y2="${y + DLAYOUT.HEADER_H}" stroke="${FRAME}" stroke-opacity="0.25" stroke-width="1"/>`;
  s += iconAt(d.node.data?.deviceType, x + 10, y + (DLAYOUT.HEADER_H - 26) / 2, 26);
  s += `<text x="${x + 44}" y="${y + DLAYOUT.HEADER_H / 2 + 5}" font-family="IBM Plex Sans, sans-serif" font-size="13" font-weight="600" fill="${INK}">${esc(trunc(name, 18))}</text>`;
  if (vlan) s += `<circle cx="${x + w - 14}" cy="${y + DLAYOUT.HEADER_H / 2}" r="5" fill="${vlan.color}"/>`;
  if (!d.ports.length) {
    s += `<text x="${x + 12}" y="${y + DLAYOUT.HEADER_H + rowH / 2 + 4}" font-family="IBM Plex Sans, sans-serif" font-size="10" fill="${MUTED}">${esc(lang === 'en' ? 'No ports' : 'Keine Ports')}</text>`;
  } else {
    const resolved = resolvePortVlans(d.node, doc);
    d.ports.forEach((p, i) => {
      const ry = y + DLAYOUT.HEADER_H + i * rowH;
      const cy = ry + rowH / 2;
      if (i > 0) s += `<line x1="${x}" y1="${ry}" x2="${x + w}" y2="${ry}" stroke="${FRAME}" stroke-opacity="0.12" stroke-width="1"/>`;
      const r = resolved[p.id] || {};
      const dot = r.color || FRAME;
      const tagged = r.taggedVlans || [];
      const untaggedV = r.untaggedVlan || null;
      const title = resolvedPortTitle(r, p.name, lang);
      if (r.color && r.fromUntagged) {
        s += `<rect x="${x + 2}" y="${ry + 3}" width="3" height="${Math.max(4, rowH - 6)}" rx="1.5" fill="${r.color}"/>`;
      }
      s += `<circle cx="${x + 11}" cy="${cy}" r="2.6" fill="${dot}"/>`;
      s += `<circle cx="${x + w - 11}" cy="${cy}" r="2.6" fill="${dot}"/>`;
      const hasBadge = showBadges && (untaggedV || tagged.length);
      const nameMax = hasBadge ? 11 : 20;
      s += `<text x="${x + 20}" y="${cy + font / 3}" font-family="IBM Plex Mono, monospace" font-size="${font}" fill="#2A3138">${esc(trunc(p.name, nameMax))}</text>`;
      if (showBadges) {
        let bx = x + w - 16;
        if (tagged.length) {
          const tg = 'T ' + tagged.map((v) => v.vlanId).join(',');
          s += `<text x="${bx}" y="${cy + badgeFont / 3}" text-anchor="end" font-family="IBM Plex Mono, monospace" font-size="${badgeFont}" fill="${MUTED}">${esc(trunc(tg, 12))}</text>`;
          bx -= Math.min(tg.length, 12) * (badgeFont * 0.62) + 6;
        }
        if (untaggedV) {
          s += `<text x="${bx}" y="${cy + badgeFont / 3}" text-anchor="end" font-family="IBM Plex Mono, monospace" font-size="${badgeFont}" font-weight="700" fill="${untaggedV.color}">U${untaggedV.vlanId}</text>`;
        }
      }
      s += `<rect x="${x}" y="${ry}" width="${w}" height="${rowH}" fill="transparent"><title>${esc(title)}</title></rect>`;
    });
  }
  return s;
}

// ---- Panel view (front-panel representation, shared by canvas + PDF) -------
// Per-port VLAN colouring (prefer explicit untagged VLAN, fall back to the peer
// device's VLAN) lives in ./vlanPorts (portColorMap).

export function renderPanelInnerSVG(node, doc, lang, opts = {}) {
  const showBadges = opts.showBadges !== false;
  const L = computePanelLayout(node, { sideHints: opts.sideHints });
  const vlan = doc.vlans.find((v) => v.id === node.data?.vlanRef);
  const name = node.data?.label || deviceLabel(node.data?.deviceType, lang);
  const resolved = resolvePortVlans(node, doc);
  const w = L.width;
  const h = L.height;
  let s = `<rect x="0" y="0" width="${w}" height="${h}" rx="9" fill="#ffffff" stroke="${FRAME}" stroke-width="1.3"/>`;
  // Left info column (fixed width): icon, wrapped device name, IP. Keeps the port matrix clear of the label.
  const lw = L.labelW;
  s += `<rect x="1" y="1" width="${lw - 1}" height="${h - 2}" rx="8" fill="${INK}" fill-opacity="0.02"/>`;
  s += `<line x1="${lw}" y1="0" x2="${lw}" y2="${h}" stroke="${FRAME}" stroke-opacity="0.22" stroke-width="1"/>`;
  s += iconAt(node.data?.deviceType, 8, P.TOP_PAD, P.ICON);
  if (vlan) s += `<circle cx="${lw - 12}" cy="${P.TOP_PAD + 8}" r="4.5" fill="${vlan.color}" stroke="#ffffff" stroke-width="1"/>`;
  const nameLines = wrapText(name, P.NAME_CHARS, P.NAME_MAX_LINES);
  const nameTop = P.TOP_PAD + P.ICON + 6;
  nameLines.forEach((ln, i) => {
    s += `<text x="8" y="${nameTop + 10 + i * P.NAME_LH}" font-family="IBM Plex Sans, sans-serif" font-size="${P.NAME_FS}" font-weight="600" fill="${INK}">${esc(ln)}</text>`;
  });
  const ipVal = node.data?.ip || '';
  if (ipVal) {
    const ipY = nameTop + 10 + (Math.max(1, nameLines.length) - 1) * P.NAME_LH + 4 + P.IP_LH - 3;
    s += `<text x="8" y="${ipY}" font-family="IBM Plex Mono, monospace" font-size="10" fill="${MUTED}">${esc(trunc(ipVal, 17))}</text>`;
  }

  L.groups.forEach((g) => {
    // Port prefix sits in the group's left column, vertically centred on the
    // row(s) (once, even for two-row layouts). Replaces the old direction arrow.
    const py = g.rows === 2 ? (L.topRowY + L.bottomRowY + P.CELL_H) / 2 : L.topRowY + P.CELL_H / 2;
    s += `<text x="${g.x + P.PREFIX_W / 2}" y="${py + 3.4}" text-anchor="middle" font-family="IBM Plex Mono, monospace" font-size="9.5" font-weight="700" fill="${MUTED}">${esc(trunc(g.prefix || '\u2014', 6))}</text>`;
    g.cells.forEach((c) => {
      if (c.dual) {
        // Patch panel port: a stub line UP and DOWN (pass-through look).
        s += `<line x1="${c.anchorX}" y1="0" x2="${c.anchorX}" y2="${c.y}" stroke="${FRAME}" stroke-opacity="0.45" stroke-width="1"/>`;
        s += `<line x1="${c.anchorX}" y1="${c.y + c.h}" x2="${c.anchorX}" y2="${h}" stroke="${FRAME}" stroke-opacity="0.45" stroke-width="1"/>`;
        return;
      }
      if (c.side === 'top') s += `<line x1="${c.anchorX}" y1="0" x2="${c.anchorX}" y2="${c.y}" stroke="${FRAME}" stroke-opacity="0.45" stroke-width="1"/>`;
      else s += `<line x1="${c.anchorX}" y1="${c.y + c.h}" x2="${c.anchorX}" y2="${h}" stroke="${FRAME}" stroke-opacity="0.45" stroke-width="1"/>`;
    });
    g.cells.forEach((c) => {
      const num = c.port.num != null ? c.port.num : c.port.name;
      const r = resolved[c.port.id] || {};
      const col = r.color;
      const fill = col || '#F4F5F3';
      const fillOp = col ? 0.22 : 1;
      const stroke = col || FRAME;
      const sw = col ? 1.4 : 1;
      const tagged = r.taggedVlans || [];
      const title = resolvedPortTitle(r, c.port.name, lang);
      s += `<rect x="${c.x}" y="${c.y}" width="${c.w}" height="${c.h}" rx="2.5" fill="${fill}" fill-opacity="${fillOp}" stroke="${stroke}" stroke-width="${sw}"><title>${esc(title)}</title></rect>`;
      s += `<text x="${c.x + c.w / 2}" y="${c.y + c.h / 2 + 3.5}" text-anchor="middle" font-family="IBM Plex Mono, monospace" font-size="10" fill="#2A3138">${esc(trunc(String(num), 5))}</text>`;
      if (showBadges && tagged.length) {
        // trunk indicator: small corner triangle (tagged VLANs present)
        s += `<path d="M${c.x + c.w - 6} ${c.y} L${c.x + c.w} ${c.y} L${c.x + c.w} ${c.y + 6} Z" fill="${FRAME}" fill-opacity="0.6"><title>${esc(title)}</title></path>`;
      }
    });
  });

  // Config-group markers: a dashed coloured box around each port group's cells
  // plus a small group label. Tagged VLANs are listed in the printed legend.
  const cellById = {};
  L.groups.forEach((g) => g.cells.forEach((c) => (cellById[c.port.id] = c)));
  (node.data?.portGroups || []).forEach((grp) => {
    const cells = (grp.memberIds || []).map((id) => cellById[id]).filter(Boolean);
    if (!cells.length) return;
    const u = doc.vlans.find((v) => v.id === grp.untaggedVlanRef);
    const gColor = u ? u.color : '#0B5FFF';
    // Segment the dashed frame so it hugs only the actual cells: group members
    // by row, split each row into contiguous column runs (gaps break the frame),
    // then merge two rows into one block only when they share the exact same
    // column span. This avoids covering empty cells (e.g. 3 ports in a 2-row
    // layout) and splits non-contiguous ports into separate blocks.
    const colStep = P.CELL_W + P.GAP_X;
    const byRow = new Map();
    cells.forEach((c) => {
      if (!byRow.has(c.y)) byRow.set(c.y, []);
      byRow.get(c.y).push(c);
    });
    let runs = [];
    Array.from(byRow.keys())
      .sort((a, b) => a - b)
      .forEach((y) => {
        const row = byRow.get(y).sort((a, b) => a.x - b.x);
        let run = [row[0]];
        for (let i = 1; i < row.length; i++) {
          const prev = run[run.length - 1];
          if (row[i].x - prev.x <= colStep + 1) run.push(row[i]);
          else {
            runs.push(run);
            run = [row[i]];
          }
        }
        runs.push(run);
      });
    let rects = runs.map((run) => {
      const x = Math.min(...run.map((c) => c.x));
      const xMax = Math.max(...run.map((c) => c.x + c.w));
      const y = run[0].y;
      return { x, y, w: xMax - x, h: P.CELL_H };
    });
    // Vertical merge: a top-row block + bottom-row block with the identical
    // column span (a full filled rectangle) becomes a single rect.
    const merged = [];
    const used = new Set();
    rects.forEach((r, i) => {
      if (used.has(i)) return;
      let cur = r;
      rects.forEach((o, j) => {
        if (j === i || used.has(j)) return;
        if (Math.abs(o.x - cur.x) < 1 && Math.abs(o.w - cur.w) < 1 && o.y > cur.y) {
          cur = { x: cur.x, y: cur.y, w: cur.w, h: o.y + o.h - cur.y };
          used.add(j);
        }
      });
      merged.push(cur);
    });
    // Anchor the label at the top-left-most block.
    const labelBlock = merged.reduce((a, b) => (b.y < a.y || (b.y === a.y && b.x < a.x) ? b : a), merged[0]);
    merged.forEach((r) => {
      s += `<rect x="${r.x - 2.5}" y="${r.y - 2.5}" width="${r.w + 5}" height="${r.h + 5}" rx="3.5" fill="none" stroke="${gColor}" stroke-width="1.3" stroke-dasharray="3 2"><title>${esc(grp.name || '')}</title></rect>`;
    });
    s += `<text x="${labelBlock.x - 2}" y="${labelBlock.y - 5}" font-family="IBM Plex Sans, sans-serif" font-size="8" font-weight="700" fill="${gColor}">${esc(trunc(grp.name || 'G', 14))}</text>`;
  });

  if (!L.groups.length) {
    const cx = lw + (w - lw) / 2;
    s += `<text x="${cx}" y="${h / 2 + 4}" text-anchor="middle" font-family="IBM Plex Sans, sans-serif" font-size="11" fill="${MUTED}">${esc(lang === 'en' ? 'No ports' : 'Keine Ports')}</text>`;
  }
  return { svg: s, width: w, height: h };
}

function panelEdgePath(s, t, connType, laneOff = 0) {
  const off = 22;
  const sHoriz = s.side === 'left' || s.side === 'right';
  const tHoriz = t.side === 'left' || t.side === 'right';
  const sDir = s.side === 'top' || s.side === 'left' ? -1 : 1;
  const tDir = t.side === 'top' || t.side === 'left' ? -1 : 1;
  let pts;
  if (!sHoriz && !tHoriz) {
    // both endpoints exit vertically (top/bottom) — shared horizontal corridor.
    // Clamp the lane offset so the corridor stays between the stub-gapped
    // endpoints (otherwise a short vertical run bends the cable backwards).
    const s1 = s.y + sDir * off;
    const t1 = t.y + tDir * off;
    const lo = Math.min(s1, t1);
    const hi = Math.max(s1, t1);
    const midY = Math.round(Math.max(lo, Math.min(hi, (s1 + t1) / 2 + laneOff)));
    pts = `${s.x},${s.y} ${s.x},${midY} ${t.x},${midY} ${t.x},${t.y}`;
  } else if (sHoriz && tHoriz) {
    // both endpoints exit horizontally (left/right) — shared vertical corridor.
    // Clamp the lane offset to the stub-gapped span (see vertical case above).
    const s1 = s.x + sDir * off;
    const t1 = t.x + tDir * off;
    const lo = Math.min(s1, t1);
    const hi = Math.max(s1, t1);
    const midX = Math.round(Math.max(lo, Math.min(hi, (s1 + t1) / 2 + laneOff)));
    pts = `${s.x},${s.y} ${midX},${s.y} ${midX},${t.y} ${t.x},${t.y}`;
  } else if (sHoriz) {
    // source horizontal (patch panel), target vertical — L-shape via target column
    pts = `${s.x},${s.y} ${s.x + sDir * off},${s.y} ${t.x},${s.y} ${t.x},${t.y}`;
  } else {
    // source vertical, target horizontal (patch panel) — L-shape via source column
    pts = `${s.x},${s.y} ${s.x},${t.y} ${t.x + tDir * off},${t.y} ${t.x},${t.y}`;
  }
  return `<polyline points="${pts}" fill="none" ${connSvgAttrs(connType)} stroke-linejoin="round" stroke-linecap="round"/>`;
}

function renderDetailPanelBody(doc, page, model, lang, opts = {}) {
  const sideHints = computePanelSideHints(model);
  const layouts = {};
  model.devices.forEach((d) => (layouts[d.deviceId] = computePanelLayout(d.node, { sideHints: sideHints[d.deviceId] })));
  const anchorOf = (deviceId, portId, peerId) => {
    const d = model.devices.find((x) => x.deviceId === deviceId);
    const L = layouts[deviceId];
    if (!d || !L || !L.anchors[portId]) return null;
    const a = L.anchors[portId];
    const ox = Math.round(d.position.x);
    const oy = Math.round(d.position.y);
    if (a.dual) {
      // Patch panel: dock to the TOP/BOTTOM side nearest the peer (by Y).
      const peer = peerId ? model.devices.find((x) => x.deviceId === peerId) : null;
      const peerL = peer ? layouts[peerId] : null;
      const peerCenterY = peer
        ? Math.round(peer.position.y) + (peerL?.height || 0) / 2
        : oy + (a.topY + a.bottomY) / 2;
      const side = pickBothSidesSide(d.position, a, peerCenterY);
      const py = side === 'bottom' ? a.bottomY : a.topY;
      return { x: ox + a.anchorX, y: oy + py, side };
    }
    return { x: ox + a.x, y: oy + a.y, side: a.side };
  };
  let body = '';
  const pairKey = (a, b) => [a, b].sort().join('|');
  const groups = {};
  model.internalEdges.forEach((e) => {
    const k = pairKey(e.sourceDeviceId, e.targetDeviceId);
    (groups[k] = groups[k] || []).push(e.id);
  });
  model.internalEdges.forEach((e) => {
    const s = anchorOf(e.sourceDeviceId, e.sourcePortId, e.targetDeviceId);
    const t = anchorOf(e.targetDeviceId, e.targetPortId, e.sourceDeviceId);
    if (s && t) {
      const sib = groups[pairKey(e.sourceDeviceId, e.targetDeviceId)] || [e.id];
      const laneOff = sib.length > 1 ? (sib.indexOf(e.id) - (sib.length - 1) / 2) * 12 : 0;
      body += panelEdgePath(s, t, e.connType, laneOff);
    }
  });
  if (page.showStubs) {
    model.stubs.forEach((st) => {
      const L = layouts[st.deviceId];
      const rawA = L?.anchors?.[st.portId];
      const d = model.devices.find((x) => x.deviceId === st.deviceId);
      if (!rawA || !d) return;
      const oName = deviceDisplayName(doc, st.otherDeviceId, lang);
      const oPort = portNameGlobal(doc, st.otherDeviceId, st.otherPortId);
      const txt = oPort ? `${oName} \u00b7 ${oPort}` : oName;
      // Patch panel (dual): dock the external stub on the BOTTOM side.
      const a = rawA.dual
        ? { x: Math.round(d.position.x) + rawA.anchorX, y: Math.round(d.position.y) + rawA.bottomY, side: 'bottom' }
        : { x: Math.round(d.position.x) + rawA.x, y: Math.round(d.position.y) + rawA.y, side: rawA.side };
      const top = a.side === 'top';
      const stubLen = 13;
      const ey = a.y + (top ? -stubLen : stubLen);
      body += `<polyline points="${a.x},${a.y} ${a.x},${ey}" fill="none" ${connSvgAttrs(st.connType)} stroke-linecap="round"/>`;
      // Rotated 90° label so it stays within the port column (one label per
      // port, side by side) instead of a wide box that overlaps neighbours.
      const L2 = 116;
      const BW = 15;
      const rot = top ? -90 : 90;
      body += `<g transform="translate(${a.x},${ey}) rotate(${rot})">`;
      body += `<rect x="0" y="${-BW / 2}" width="${L2}" height="${BW}" rx="4" fill="#ffffff" stroke="${MUTED}" stroke-width="1" stroke-dasharray="3 2"/>`;
      body += `<text x="6" y="3.4" font-family="IBM Plex Sans, sans-serif" font-size="9" font-weight="600" fill="${INK}">${esc(trunc(txt, 22))}</text>`;
      body += `</g>`;
    });
  }
  model.devices.forEach((d) => {
    const inner = renderPanelInnerSVG(d.node, doc, lang, { ...opts, sideHints: sideHints[d.deviceId] });
    body += `<g transform="translate(${Math.round(d.position.x)},${Math.round(d.position.y)})">${inner.svg}</g>`;
  });
  if (!model.devices.length) {
    body += `<text x="${SHEET.w / 2}" y="${SHEET.h / 2}" text-anchor="middle" font-family="IBM Plex Sans, sans-serif" font-size="18" fill="${MUTED}">${esc(lang === 'en' ? 'No devices added.' : 'Noch keine Ger\u00e4te hinzugef\u00fcgt.')}</text>`;
  }
  return body;
}

// Compact a list of port names into ranges where they share a textual prefix
// and have consecutive numeric suffixes, e.g. ["eth1","eth2","eth3","eth7"]
// -> "eth1–eth3, eth7". Names without a numeric suffix are kept verbatim.
function compactPortNames(names) {
  const parsed = (names || []).map((n) => {
    const m = /^(.*?)(\d+)$/.exec(n || '');
    return m ? { prefix: m[1], num: parseInt(m[2], 10), raw: n } : { prefix: null, raw: n };
  });
  const numeric = parsed.filter((p) => p.prefix !== null);
  const plain = parsed.filter((p) => p.prefix === null);
  const byPrefix = {};
  const order = [];
  numeric.forEach((p) => {
    if (!byPrefix[p.prefix]) {
      byPrefix[p.prefix] = [];
      order.push(p.prefix);
    }
    byPrefix[p.prefix].push(p.num);
  });
  const parts = [];
  order.forEach((prefix) => {
    const nums = [...new Set(byPrefix[prefix])].sort((a, b) => a - b);
    let i = 0;
    while (i < nums.length) {
      let j = i;
      while (j + 1 < nums.length && nums[j + 1] === nums[j] + 1) j++;
      if (j > i + 1) parts.push(`${prefix}${nums[i]}\u2013${prefix}${nums[j]}`);
      else for (let k = i; k <= j; k++) parts.push(`${prefix}${nums[k]}`);
      i = j + 1;
    }
  });
  plain.forEach((p) => parts.push(p.raw));
  return parts.join(', ');
}

// Optional VLAN legend block (top-right): lists port groups + individually
// configured ports per device with their untagged/tagged VLANs. Bounded height.
// Collect VLAN/port legend entries: per device, its port groups + individually
// configured ports with untagged/tagged VLANs.
function collectVlanLegend(model, doc, lang) {
  const vById = {};
  (doc.vlans || []).forEach((v) => (vById[v.id] = v));
  const sum = (uRef, tRefs) => {
    const u = vById[uRef];
    const tg = (tRefs || []).map((r) => vById[r]).filter(Boolean).sort((a, b) => a.vlanId - b.vlanId);
    const parts = [];
    if (u) parts.push('U' + u.vlanId);
    if (tg.length) parts.push('T' + tg.map((v) => v.vlanId).join(','));
    return { text: parts.join('  '), color: u ? u.color : MUTED, has: !!(u || tg.length) };
  };
  const entries = [];
  model.devices.forEach((d) => {
    const node = d.node;
    const ports = node.data?.ports || [];
    const portName = (id) => (ports.find((p) => p.id === id) || {}).name || '';
    const groups = node.data?.portGroups || [];
    const grouped = new Set();
    groups.forEach((g) => (g.memberIds || []).forEach((id) => grouped.add(id)));
    const rows = [];
    groups.forEach((g) => {
      const sm = sum(g.untaggedVlanRef, g.taggedVlanRefs);
      const members = (g.memberIds || []).map(portName).filter(Boolean).join(', ');
      rows.push({ grp: true, color: sm.color, label: g.name || (lang === 'en' ? 'Group' : 'Gruppe'), info: sm.text, members });
    });
    // Consolidate ungrouped individual ports that share the SAME VLAN signature
    // (identical untagged + tagged set) into a single row, e.g. "eth1–eth8".
    const sig = (uRef, tRefs) => `${uRef || ''}|${(tRefs || []).slice().sort().join(',')}`;
    const buckets = new Map();
    const bucketOrder = [];
    ports.forEach((p) => {
      if (grouped.has(p.id)) return;
      const sm = sum(p.untaggedVlanRef, p.taggedVlanRefs);
      if (!sm.has) return;
      const key = sig(p.untaggedVlanRef, p.taggedVlanRefs);
      if (!buckets.has(key)) {
        buckets.set(key, { color: sm.color, info: sm.text, names: [] });
        bucketOrder.push(key);
      }
      buckets.get(key).names.push(p.name);
    });
    bucketOrder.forEach((key) => {
      const b = buckets.get(key);
      rows.push({ grp: false, color: b.color, label: compactPortNames(b.names), info: b.info, members: '' });
    });
    if (rows.length) entries.push({ name: node.data?.label || deviceLabel(node.data?.deviceType, lang), rows });
  });
  return entries;
}

// VLAN/port legend box — same visual language as the cabling legend, anchored
// bottom-left and growing upward from `bottomY` (so it can stack above the
// connection-type legend). Returns '' when there is nothing to show.
function vlanLegendBox(entries, lang, bottomY) {
  if (!entries || !entries.length) return '';
  const W = 300;
  const PADX = 12;
  const LH = 15;
  const MAX = 30;
  const lines = [];
  for (const e of entries) {
    if (lines.length >= MAX) break;
    lines.push({ kind: 'device', text: e.name });
    for (const r of e.rows) {
      if (lines.length >= MAX) break;
      lines.push({ kind: 'row', r });
    }
  }
  const truncated = entries.reduce((n, e) => n + 1 + e.rows.length, 0) > lines.length;
  const shownCount = lines.length + (truncated ? 1 : 0);
  const H = 30 + shownCount * LH + 8;
  const x = INNER + 12;
  const y = bottomY - H;
  let s = `<rect x="${x}" y="${y}" width="${W}" height="${H}" rx="6" fill="#ffffff" stroke="${FRAME}" stroke-width="1"/>`;
  s += `<text x="${x + 12}" y="${y + 19}" font-family="IBM Plex Sans, sans-serif" font-size="11" font-weight="600" letter-spacing="0.7" fill="${INK}">${esc(lang === 'en' ? 'VLAN / PORT LEGEND' : 'VLAN-/PORT-LEGENDE')}</text>`;
  s += `<line x1="${x}" y1="${y + 27}" x2="${x + W}" y2="${y + 27}" stroke="${FRAME}" stroke-opacity="0.2" stroke-width="1"/>`;
  let ly = y + 27 + 14;
  lines.forEach((ln) => {
    if (ln.kind === 'device') {
      s += `<text x="${x + 12}" y="${ly}" font-family="IBM Plex Sans, sans-serif" font-size="10.5" font-weight="700" fill="${INK}">${esc(trunc(ln.text, 38))}</text>`;
    } else {
      const r = ln.r;
      if (r.grp) {
        s += `<rect x="${x + 13}" y="${ly - 7}" width="7" height="7" rx="1.4" fill="${r.color}"/>`;
      } else {
        s += `<circle cx="${x + 16.5}" cy="${ly - 3.5}" r="3.2" fill="${r.color}"/>`;
      }
      s += `<text x="${x + 26}" y="${ly}" font-family="IBM Plex Mono, monospace" font-size="9.5" fill="#2A3138">${esc(trunc(r.label, 20))}</text>`;
      s += `<text x="${x + W - PADX}" y="${ly}" text-anchor="end" font-family="IBM Plex Mono, monospace" font-size="9.5" font-weight="600" fill="${r.color}">${esc(trunc(r.info, 16))}</text>`;
    }
    ly += LH;
  });
  if (truncated) {
    s += `<text x="${x + 12}" y="${ly}" font-family="IBM Plex Sans, sans-serif" font-size="9.5" font-style="italic" fill="${MUTED}">${esc(lang === 'en' ? '… more (see device list)' : '… weitere (siehe Geräteliste)')}</text>`;
  }
  return s;
}

// VLAN legend bottom anchor: sit just above the connection-type legend (if any).
function vlanLegendBottomY(connTypesLen) {
  const connH = connTypesLen ? 30 + connTypesLen * 20 : 0;
  return SHEET.h - INNER - 8 - (connH ? connH + 10 : 0);
}

export function renderDetailSVG(doc, page, lang, pageInfo = { index: '\u2014', total: '\u2014' }, opts = {}) {
  const model = computeDetailModel(doc, page);
  const legend = detailLegendTypes(model);
  const vlanLegend = opts.showLegend ? vlanLegendBox(collectVlanLegend(model, doc, lang), lang, vlanLegendBottomY(legend.length)) : '';
  const s = page.contentScale || 1;
  const scaled = (inner) => (s !== 1 ? `<g transform="scale(${s})">${inner}</g>` : inner);
  if (page.viewMode === 'panel') {
    return wrap(cadFrame(doc, lang, pageInfo, pageDisplayName(page.name, lang), legend) + scaled(renderDetailPanelBody(doc, page, model, lang, opts)) + vlanLegend);
  }
  const devMap = {};
  model.devices.forEach((d) => (devMap[d.deviceId] = d));
  const idxOf = (deviceId, portId) => {
    const d = devMap[deviceId];
    return d ? d.ports.findIndex((p) => p.id === portId) : -1;
  };
  let body = '';

  // internal port-to-port edges (drawn under the boxes)
  const pairKey = (a, b) => [a, b].sort().join('|');
  const eGroups = {};
  model.internalEdges.forEach((e) => {
    const k = pairKey(e.sourceDeviceId, e.targetDeviceId);
    (eGroups[k] = eGroups[k] || []).push(e.id);
  });
  model.internalEdges.forEach((e) => {
    const sd = devMap[e.sourceDeviceId];
    const td = devMap[e.targetDeviceId];
    if (!sd || !td) return;
    const si = idxOf(e.sourceDeviceId, e.sourcePortId);
    const ti = idxOf(e.targetDeviceId, e.targetPortId);
    if (si < 0 || ti < 0) return;
    // Fan out parallel cables sharing a corridor onto their own lane.
    const sib = eGroups[pairKey(e.sourceDeviceId, e.targetDeviceId)] || [e.id];
    const laneOff = sib.length > 1 ? (sib.indexOf(e.id) - (sib.length - 1) / 2) * 12 : 0;
    // Side-aware routing: side-by-side -> right/left orthogonal; stacked -> same
    // side with a vertical run just outside the boxes (mirrors the live canvas).
    const [sSide, tSide] = pickEdgeSides(sd.position, td.position);
    const sx = Math.round(sd.position.x) + (sSide === 'r' ? DLAYOUT.BOX_W : 0);
    const sy = Math.round(sd.position.y) + detailPortCenterY(si, detailRowHeight(sd.ports.length));
    const tx = Math.round(td.position.x) + (tSide === 'r' ? DLAYOUT.BOX_W : 0);
    const ty = Math.round(td.position.y) + detailPortCenterY(ti, detailRowHeight(td.ports.length));
    let pts;
    if (sSide === tSide) {
      const dir = sSide === 'r' ? 1 : -1;
      const ex = (sSide === 'r' ? Math.max(sx, tx) : Math.min(sx, tx)) + dir * 28 + laneOff;
      pts = `${sx},${sy} ${ex},${sy} ${ex},${ty} ${tx},${ty}`;
    } else {
      // Opposite sides — vertical corridor between the boxes. Clamp the lane
      // offset so the corridor stays between the two endpoints; on a short
      // (slightly-offset) run this stops the cable bending backwards.
      const lo = Math.min(sx, tx);
      const hi = Math.max(sx, tx);
      const mx = Math.round(Math.max(lo, Math.min(hi, (sx + tx) / 2 + laneOff)));
      pts = `${sx},${sy} ${mx},${sy} ${mx},${ty} ${tx},${ty}`;
    }
    body += `<polyline points="${pts}" fill="none" ${connSvgAttrs(e.connType)} stroke-linejoin="round" stroke-linecap="round"/>`;
  });

  // external connection stubs
  if (page.showStubs) {
    model.stubs.forEach((st) => {
      const d = devMap[st.deviceId];
      if (!d) return;
      const i = idxOf(st.deviceId, st.portId);
      if (i < 0) return;
      const sx = Math.round(d.position.x) + DLAYOUT.BOX_W;
      const sy = Math.round(d.position.y) + detailPortCenterY(i, detailRowHeight(d.ports.length));
      const ex = sx + 64;
      body += `<polyline points="${sx},${sy} ${ex},${sy}" fill="none" ${connSvgAttrs(st.connType)} stroke-linecap="round"/>`;
      const oName = deviceDisplayName(doc, st.otherDeviceId, lang);
      const oPort = portNameGlobal(doc, st.otherDeviceId, st.otherPortId);
      const bw = 134;
      const bh = oPort ? 32 : 20;
      const bx = ex;
      const by = sy - bh / 2;
      body += `<rect x="${bx}" y="${by}" width="${bw}" height="${bh}" rx="5" fill="#ffffff" stroke="${MUTED}" stroke-width="1" stroke-dasharray="3 2"/>`;
      body += `<text x="${bx + 8}" y="${by + 13}" font-family="IBM Plex Sans, sans-serif" font-size="10" font-weight="600" fill="${INK}">${esc(trunc(oName, 19))}</text>`;
      if (oPort) body += `<text x="${bx + 8}" y="${by + 26}" font-family="IBM Plex Mono, monospace" font-size="9.5" fill="${MUTED}">${esc(trunc(oPort, 19))}</text>`;
    });
  }

  // device boxes (drawn over edges)
  model.devices.forEach((d) => (body += detailDeviceSvg(d, doc, lang, opts)));

  if (!model.devices.length) {
    body += `<text x="${SHEET.w / 2}" y="${SHEET.h / 2}" text-anchor="middle" font-family="IBM Plex Sans, sans-serif" font-size="18" fill="${MUTED}">${esc(lang === 'en' ? 'No devices added.' : 'Noch keine Ger\u00e4te hinzugef\u00fcgt.')}</text>`;
  }

  return wrap(cadFrame(doc, lang, pageInfo, pageDisplayName(page.name, lang), legend) + scaled(body) + vlanLegend);
}

// Frame + title block (+ legend) only \u2014 the live canvas "sheet" behind device nodes.
export function renderFrameOnlySVG(doc, lang, pageInfo, heading, opts = {}) {
  const connTypes = usedConnTypes(doc);
  let extra = '';
  if (opts.showLegend && opts.page && opts.page.type === 'detail') {
    const model = computeDetailModel(doc, opts.page);
    extra = vlanLegendBox(collectVlanLegend(model, doc, lang), lang, vlanLegendBottomY(connTypes.length));
  }
  return wrap(cadFrame(doc, lang, pageInfo, heading, connTypes, { transparent: !!opts.transparent }) + extra);
}

// Location plan ("Positionsplan"): a physical background (image / PDF raster /
// OSM snapshot, all stored as one data URI) with small device markers + their
// connections overlaid. White base + content first, then the CAD frame + title
// block painted ON TOP (transparent) so the title block stays visible over the
// background image.
export function renderLocationSVG(doc, page, lang, pageInfo = { index: '\u2014', total: '\u2014' }) {
  const innerW = SHEET.w - 2 * INNER;
  const innerH = SHEET.h - 2 * INNER;
  const bg = page.background || {};
  // Element zoom scales ONLY the markers; background + frame stay fixed to the sheet.
  const s = page.contentScale || 1;
  const MW = 46 * s;
  const labelFont = Math.max(9, Math.round(11 * s));
  let body = '';
  const bgHref = safeImageHref(bg.dataUri);
  if (bgHref) {
    body += `<image x="${INNER}" y="${INNER}" width="${innerW}" height="${innerH}" preserveAspectRatio="xMidYMid meet" href="${esc(bgHref)}"/>`;
  }
  const markers = (page.markers || [])
    .map((m) => ({ m, node: findDeviceGlobal(doc, m.deviceId) }))
    .filter((x) => x.node);
  const byId = {};
  markers.forEach((mk) => (byId[mk.m.deviceId] = mk));
  const set = new Set(markers.map((mk) => mk.m.deviceId));
  const center = (mk) => ({ x: Math.round(mk.m.position?.x || 0) + MW / 2, y: Math.round(mk.m.position?.y || 0) + MW / 2 });
  if (page.showConnections !== false) {
    getAllEdges(doc).forEach((e) => {
      if (set.has(e.source) && set.has(e.target)) {
        const a = center(byId[e.source]);
        const b = center(byId[e.target]);
        body += `<line x1="${a.x}" y1="${a.y}" x2="${b.x}" y2="${b.y}" ${connSvgAttrs(e.connType)} stroke-linecap="round"/>`;
      }
    });
  }
  markers.forEach(({ m, node }) => {
    const x = Math.round(m.position?.x || 0);
    const y = Math.round(m.position?.y || 0);
    body += `<rect x="${x - 3}" y="${y - 3}" width="${MW + 6}" height="${MW + 6}" rx="8" fill="#ffffff" stroke="${FRAME}" stroke-opacity="0.45" stroke-width="1"/>`;
    body += iconAt(node.data?.deviceType, x, y, MW);
    const name = node.data?.label || deviceLabel(node.data?.deviceType, lang);
    const label = trunc(name, 24);
    const lx = x + MW / 2;
    const lrY = y + MW + 3;
    const lrH = labelFont + 6;
    const lrW = Math.max(MW, label.length * labelFont * 0.6 + 12);
    // Solid white pill behind the label keeps it readable over any background.
    // (Do NOT rely on paint-order stroke halos — svg2pdf/cairosvg ignore them on
    // tspans, which made the white outline overpaint the dark text in PDFs.)
    body += `<rect x="${(lx - lrW / 2).toFixed(1)}" y="${lrY}" width="${lrW.toFixed(1)}" height="${lrH}" rx="3" fill="#ffffff" fill-opacity="0.9" stroke="${FRAME}" stroke-opacity="0.15" stroke-width="1"/>`;
    body += `<text x="${lx}" y="${(lrY + lrH / 2 + labelFont * 0.34).toFixed(1)}" text-anchor="middle" font-family="IBM Plex Sans, sans-serif" font-size="${labelFont}" font-weight="600" fill="${INK}">${esc(label)}</text>`;
  });
  return wrap(
    `<rect x="0" y="0" width="${SHEET.w}" height="${SHEET.h}" fill="#ffffff"/>` +
      body +
      cadFrame(doc, lang, pageInfo, pageDisplayName(page.name, lang), usedConnTypes(doc), { transparent: true })
  );
}

// Build the ordered list of printable pages (device pages + auto pages). The two
// auto pages (logical diagram + VLAN list) may each expand into multiple A3
// sheets; the title-block "SHEET X / Y" reflects the real total across all sheets.
export function buildAllPageSVGs(rawDoc, lang, opts = {}) {
  // Defense in depth: strip non-image URIs / malformed colors before rendering
  // (documents may originate from imports or decrypted share payloads).
  const doc = sanitizeDocImages(rawDoc);
  const basePages = doc.pages || [];
  const cablePlan = planCableListPages(doc, lang);
  const vlanPlan = planVlanListPages(doc, lang);
  const total = basePages.length + cablePlan.length + vlanPlan.length;
  const out = [];

  basePages.forEach((p, i) => {
    const info = { index: i + 1, total };
    const nm = pageDisplayName(p.name, lang);
    if (p.type === 'detail') out.push({ name: nm, svg: renderDetailSVG(doc, p, lang, info, opts) });
    else if (p.type === 'location') out.push({ name: nm, svg: renderLocationSVG(doc, p, lang, info) });
    else out.push({ name: nm, svg: renderPageSVG(p, doc, lang, info, opts) });
  });

  const cableName = lang === 'en' ? 'Cable list' : 'Kabelliste';
  cablePlan.forEach((pg, i) => {
    const index = basePages.length + 1 + i;
    const name = cablePlan.length > 1 ? `${cableName} (${i + 1}/${cablePlan.length})` : cableName;
    out.push({ name, svg: renderCableListPage(pg, doc, lang, { index, total }) });
  });

  const vlanBase = basePages.length + cablePlan.length;
  const vlanName = lang === 'en' ? 'VLAN list' : 'VLAN-Liste';
  vlanPlan.forEach((pg, i) => {
    const index = vlanBase + 1 + i;
    const name = vlanPlan.length > 1 ? `${vlanName} (${i + 1}/${vlanPlan.length})` : vlanName;
    out.push({ name, svg: renderVlanListPage(pg, doc, lang, { index, total }) });
  });

  return out;
}
